import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Button,
  Input,
  Modal,
  Select,
  Spin,
  Splitter,
  Table,
  Tooltip,
  message
} from "antd";
import {
  deleteSprNumber,
  fetchSprNumberBranches,
  fetchSprNumberCategories,
  fetchSprNumberList
} from "./spr-number-api";
import SprNumber from "./spr-number";
import SprNumberFillModal from "./spr-number-fill-modal";
import {
  ROOT_UPPER,
  attachLeafCounts,
  buildGroupTree,
  buildNumberTree,
  collectGroupKeys,
  countDescendantLeaves,
  findInTree,
  flattenParentOptions,
  formatCapacity,
  formatDate,
  formatSquare,
  formatTime,
  measureContentColWidth,
  isUnderGroup,
  normalizeRow,
  pick,
  roomStatus,
  toNumber
} from "./spr-number-utils";
import GridSettings, { applyGridSettings, loadGridSettings } from "@controls/grid-settings";
import chevronSrc from "./assets/chevron-down.svg";
import dotSrc from "./assets/dot.svg";
import folderSrc from "./assets/folder.svg";
import folderOpenSrc from "./assets/folder-open.svg";
import searchSrc from "./assets/search.svg";
import "./spr-numbers.css";

const GROUPS_WIDTH_KEY = "planOnline.sprNumber.groupsWidth";
const EXPANDED_KEYS_KEY = "planOnline.sprNumber.expandedKeys";
const GRID_STORAGE_KEY = "planOnline.sprNumber.gridSettings.v2";
const ID_GROUP_URL_KEY = "idGroup";
const DRAWER_URL_KEYS = ["upper", "group"];
const SEARCH_DEBOUNCE_MS = 300;
const GROUPS_WIDTH_DEFAULT = 220;
const GROUPS_WIDTH_MIN = 200;
const GROUPS_WIDTH_MAX = 280;
const TABLE_HEADER_H = 32;

const STATUS_LABEL = {
  online: "Онлайн",
  active: "Активен",
  inactive: "Неактивен"
};

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const loadGroupsWidth = () => {
  try {
    const n = Number(localStorage.getItem(GROUPS_WIDTH_KEY));
    if (!Number.isFinite(n)) return GROUPS_WIDTH_DEFAULT;
    return Math.min(GROUPS_WIDTH_MAX, Math.max(GROUPS_WIDTH_MIN, Math.round(n)));
  } catch {
    return GROUPS_WIDTH_DEFAULT;
  }
};

const defaultListFilters = () => ({
  idGroup: ROOT_UPPER,
  q: "",
  idNumberCat: 0,
  onlyActive: true,
  idBranch: null
});

const filtersFromSearchParams = (params) => {
  const src =
    params instanceof URLSearchParams ? params : new URLSearchParams(params || "");
  const base = defaultListFilters();
  const idGroupRaw = Number(src.get(ID_GROUP_URL_KEY));
  const idGroup = Number.isFinite(idGroupRaw) && idGroupRaw > 0 ? idGroupRaw : ROOT_UPPER;
  const q = src.has("q") ? src.get("q") ?? "" : "";
  const catRaw = Number(src.get("idNumberCat"));
  const idNumberCat = Number.isFinite(catRaw) && catRaw > 0 ? catRaw : 0;
  let onlyActive = base.onlyActive;
  if (src.has("active")) {
    const a = src.get("active");
    onlyActive = a === "1" || a === "true";
  }
  const branchRaw = Number(src.get("idBranch"));
  const idBranch = Number.isFinite(branchRaw) && branchRaw > 0 ? branchRaw : null;
  return { idGroup, q, idNumberCat, onlyActive, idBranch };
};

const filtersToSearchParams = (filters, preserve) => {
  const params = new URLSearchParams();
  if (preserve) {
    const src =
      preserve instanceof URLSearchParams ? preserve : new URLSearchParams(preserve || "");
    DRAWER_URL_KEYS.forEach((key) => {
      if (src.has(key)) params.set(key, src.get(key));
    });
  }
  const idGroup = Number(filters?.idGroup) || ROOT_UPPER;
  if (idGroup > 0) params.set(ID_GROUP_URL_KEY, String(idGroup));
  const q = typeof filters?.q === "string" ? filters.q.trim() : "";
  if (q) params.set("q", q);
  const idNumberCat = Number(filters?.idNumberCat) || 0;
  if (idNumberCat > 0) params.set("idNumberCat", String(idNumberCat));
  if (filters?.onlyActive) params.set("active", "1");
  else params.set("active", "0");
  const idBranch = Number(filters?.idBranch) || 0;
  if (idBranch > 0) params.set("idBranch", String(idBranch));
  return params;
};

const listSearchString = (filters, extra) => {
  const params = filtersToSearchParams(filters);
  if (extra && typeof extra === "object") {
    Object.entries(extra).forEach(([key, value]) => {
      if (value == null || value === "") params.delete(key);
      else params.set(key, String(value));
    });
  }
  const s = params.toString();
  return s ? `?${s}` : "";
};

const loadExpandedKeys = () => {
  try {
    const raw = localStorage.getItem(EXPANDED_KEYS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(String).filter((k) => k && k !== "root");
  } catch {
    return [];
  }
};

const saveExpandedKeys = (keys) => {
  try {
    const next = (keys || []).map(String).filter((k) => k && k !== "root");
    localStorage.setItem(EXPANDED_KEYS_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
};

const Chevron = () => (
  <span className="spr-numbers__chevron">
    <img src={chevronSrc} alt="" />
  </span>
);

const TreeIcon = ({ src, size }) => (
  <span className="spr-numbers__tree-icon" style={{ width: size, height: size }}>
    <img src={src} alt="" />
  </span>
);

const MiniSelect = ({ autoFocus, defaultOpen, options, placeholder, value, onChange, width }) => (
  <Select
    autoFocus={autoFocus}
    defaultOpen={defaultOpen}
    showSearch
    optionFilterProp="label"
    placeholder={placeholder}
    value={value}
    options={options}
    popupMatchSelectWidth={false}
    variant="borderless"
    suffixIcon={null}
    style={{ width }}
    onChange={onChange}
  />
);

const FILTER_KIND = {
  CAT: "cat",
  ACTIVE: "active"
};

const ACTIVE_OPTIONS = [{ value: 1, label: "Активные" }];

const NumbersSearchFilter = ({
  name,
  onNameChange,
  catId,
  onCatChange,
  catOptions,
  onlyActive,
  onOnlyActiveChange
}) => {
  const [adding, setAdding] = useState(false);
  const [draftKind, setDraftKind] = useState(null);
  const [editKind, setEditKind] = useState(null);
  const [focused, setFocused] = useState(false);
  const catLabel = (catOptions || []).find((o) => o.value === catId)?.label;
  const hasCat = catId > 0 && Boolean(catLabel);

  const chips = [];
  if (hasCat) {
    chips.push({
      key: FILTER_KIND.CAT,
      kindLabel: "Категория",
      valueLabel: catLabel
    });
  }
  if (onlyActive) {
    chips.push({
      key: FILTER_KIND.ACTIVE,
      kindLabel: "Активность",
      valueLabel: "Активные"
    });
  }

  const used = new Set(chips.map((c) => c.key));
  const availableKinds = [
    { value: FILTER_KIND.CAT, label: "Категория" },
    { value: FILTER_KIND.ACTIVE, label: "Активность" }
  ].filter((k) => !used.has(k.value));
  const canAdd = availableKinds.length > 0;

  const applyKind = (kind, next) => {
    if (kind === FILTER_KIND.CAT) onCatChange(next || 0);
    else onOnlyActiveChange(next === 1);
  };

  const clearKind = (kind) => {
    if (kind === FILTER_KIND.CAT) onCatChange(0);
    else onOnlyActiveChange(false);
    if (editKind === kind) setEditKind(null);
  };

  const startAdd = () => {
    setEditKind(null);
    if (availableKinds.length === 1 && availableKinds[0].value === FILTER_KIND.ACTIVE) {
      onOnlyActiveChange(true);
      setAdding(false);
      setDraftKind(null);
      return;
    }
    setDraftKind(availableKinds.length === 1 ? availableKinds[0].value : null);
    setAdding(true);
  };

  const cancelAdd = () => {
    setAdding(false);
    setDraftKind(null);
    setEditKind(null);
  };

  const pickDraftKind = (kind) => {
    if (kind === FILTER_KIND.ACTIVE) {
      onOnlyActiveChange(true);
      cancelAdd();
      return;
    }
    setDraftKind(kind);
  };

  const pickDraftValue = (next) => {
    if (editKind) {
      applyKind(editKind, next);
      setEditKind(null);
      return;
    }
    if (draftKind == null) return;
    applyKind(draftKind, next);
    cancelAdd();
  };

  const valueOptions =
    (editKind || draftKind) === FILTER_KIND.CAT ? catOptions : ACTIVE_OPTIONS;
  const draftValue =
    editKind === FILTER_KIND.CAT && hasCat
      ? catId
      : editKind === FILTER_KIND.ACTIVE
        ? 1
        : undefined;

  return (
    <div
      className={`spr-numbers__search${focused ? " spr-numbers__search--focused" : ""}`}
      role="group"
      aria-label="Поиск и фильтр"
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
      }}
    >
      <span className="spr-numbers__search-icon">
        <img src={searchSrc} alt="" />
      </span>
      <Input
        variant="borderless"
        allowClear
        className="spr-numbers__search-input"
        value={name}
        placeholder="Поиск по номеру или категории..."
        onChange={(e) => onNameChange(e.target.value)}
      />
      {chips.map((chip) =>
        editKind === chip.key ? (
          <span key={chip.key} className="spr-numbers__search-draft">
            <span className="spr-numbers__search-chip-kind">{chip.kindLabel}</span>
            <MiniSelect
              autoFocus
              defaultOpen
              options={valueOptions}
              placeholder="Значение"
              value={draftValue}
              width={160}
              onChange={pickDraftValue}
            />
            <button
              type="button"
              className="spr-numbers__search-chip-clear"
              title="Закрыть"
              aria-label="Закрыть"
              onClick={() => setEditKind(null)}
            >
              <CloseOutlined />
            </button>
          </span>
        ) : (
          <span key={chip.key} className="spr-numbers__search-chip">
            <button
              type="button"
              className="spr-numbers__search-chip-btn"
              title="Изменить значение"
              onClick={() => {
                setAdding(false);
                setDraftKind(null);
                setEditKind(chip.key);
              }}
            >
              <span className="spr-numbers__search-chip-kind">{chip.kindLabel}</span>
              <span className="spr-numbers__search-chip-value">{chip.valueLabel}</span>
            </button>
            <button
              type="button"
              className="spr-numbers__search-chip-clear"
              title="Сбросить"
              aria-label={`Сбросить: ${chip.kindLabel}`}
              onClick={() => clearKind(chip.key)}
            >
              <CloseOutlined />
            </button>
          </span>
        )
      )}
      {adding ? (
        <span className="spr-numbers__search-draft">
          {draftKind == null ? (
            <MiniSelect
              autoFocus
              defaultOpen
              options={availableKinds}
              placeholder="Фильтр"
              value={draftKind}
              width={130}
              onChange={pickDraftKind}
            />
          ) : (
            <>
              <span className="spr-numbers__search-chip-kind">
                {availableKinds.find((k) => k.value === draftKind)?.label || "Категория"}
              </span>
              <MiniSelect
                autoFocus
                defaultOpen
                options={valueOptions}
                placeholder="Значение"
                width={160}
                onChange={pickDraftValue}
              />
            </>
          )}
          <button
            type="button"
            className="spr-numbers__search-chip-clear"
            title="Отменить"
            aria-label="Отменить добавление фильтра"
            onClick={cancelAdd}
          >
            <CloseOutlined />
          </button>
        </span>
      ) : null}
      {canAdd && !adding && !editKind ? (
        <button
          type="button"
          className="spr-numbers__search-add"
          title="Добавить фильтр"
          aria-label="Добавить фильтр"
          onClick={startAdd}
        >
          <PlusOutlined />
        </button>
      ) : null}
    </div>
  );
};

const GroupTreeList = ({
  nodes,
  depth,
  selectedGroupId,
  editId,
  expandedKeys,
  onSelect,
  onToggle
}) => (
  <div className="spr-numbers__tree-list">
    {(nodes || []).map((node) => {
      const selected = selectedGroupId === node.idNumber;
      const hasKids = Boolean(node.children?.length);
      const expanded = hasKids && expandedKeys.includes(String(node.key));
      const useFolder = depth <= 1;
      const iconSrc = useFolder ? (selected ? folderOpenSrc : folderSrc) : dotSrc;
      return (
        <div key={node.key} className="spr-numbers__tree-group">
          <button
            type="button"
            className={[
              "spr-numbers__tree-node",
              selected ? "spr-numbers__tree-node--selected" : "",
              editId != null && node.idNumber === editId ? "spr-numbers__tree-node--editing" : "",
              depth > 1 ? "spr-numbers__tree-node--sub" : ""
            ]
              .filter(Boolean)
              .join(" ")}
            title={node.name}
            onClick={() => onSelect(node.idNumber)}
            onDoubleClick={() => hasKids && onToggle(String(node.key))}
          >
            <span className="spr-numbers__tree-main">
              <span
                className="spr-numbers__tree-icon-hit"
                onClick={(e) => {
                  if (!hasKids) return;
                  e.stopPropagation();
                  onToggle(String(node.key));
                }}
              >
                <TreeIcon src={iconSrc} size={useFolder ? 16 : 8} />
              </span>
              <span className="spr-numbers__tree-name">{node.name}</span>
            </span>
            <span className="spr-numbers__tree-count">{node.leafCount ?? 0}</span>
          </button>
          {hasKids && expanded ? (
            <div className="spr-numbers__tree-sub">
              <GroupTreeList
                nodes={node.children}
                depth={depth + 1}
                selectedGroupId={selectedGroupId}
                editId={editId}
                expandedKeys={expandedKeys}
                onSelect={onSelect}
                onToggle={onToggle}
              />
            </div>
          ) : null}
        </div>
      );
    })}
  </div>
);

/**
 * Список номерного фонда: группы слева, номера выбранной группы справа.
 * Корневая группа («Все номера») — все номера-листья. Delphi: TFrmSprNumber.
 */
const SprNumbers = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = routeId === "new";
  const editId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
  const drawerOpen = isNew || editId != null;
  const newIdUpper = toNumber(searchParams.get("upper"));
  const isGroupNew = searchParams.get("group") === "1";
  const urlFilters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const selectedGroupId = urlFilters.idGroup;
  const law = useSelector((state) => state.user.session?.law);
  const canEdit = getLawFlag(law, 4) === 1;

  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [branchFilter, setBranchFilter] = useState(() => urlFilters.idBranch);
  const [catFilter, setCatFilter] = useState(() => urlFilters.idNumberCat);
  const [onlyActive, setOnlyActive] = useState(() => urlFilters.onlyActive);
  const [nameFilter, setNameFilter] = useState(() => urlFilters.q);
  const [debouncedQ, setDebouncedQ] = useState(() => (urlFilters.q || "").trim());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [checkedIds, setCheckedIds] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState(() => loadExpandedKeys());
  const [tableScrollY, setTableScrollY] = useState(400);
  const [groupsWidth, setGroupsWidth] = useState(loadGroupsWidth);
  const [fillOpen, setFillOpen] = useState(false);
  const [gridSettings, setGridSettings] = useState(() => loadGridSettings(GRID_STORAGE_KEY));
  const leavesPaneRef = useRef(null);
  const uiRestoredRef = useRef(false);
  const requestIdRef = useRef(0);
  const skipUrlSyncRef = useRef(false);

  useEffect(() => {
    try {
      localStorage.removeItem("planOnline.sprNumber.selectedGroupId");
    } catch {
      /* ignore */
    }
  }, []);

  const listFilters = useMemo(
    () => ({
      idGroup: selectedGroupId,
      q: debouncedQ,
      idNumberCat: catFilter,
      onlyActive,
      idBranch: branchFilter
    }),
    [selectedGroupId, debouncedQ, catFilter, onlyActive, branchFilter]
  );

  const listFiltersLive = useMemo(
    () => ({
      idGroup: selectedGroupId,
      q: nameFilter,
      idNumberCat: catFilter,
      onlyActive,
      idBranch: branchFilter
    }),
    [selectedGroupId, nameFilter, catFilter, onlyActive, branchFilter]
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ((nameFilter || "").trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [nameFilter]);

  useEffect(() => {
    const next = filtersToSearchParams(listFilters, searchParams);
    const nextStr = next.toString();
    if (searchParams.toString() === nextStr) return;
    skipUrlSyncRef.current = true;
    setSearchParams(next, { replace: true });
  }, [listFilters, searchParams, setSearchParams]);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    const fromUrl = filtersFromSearchParams(searchParams);
    setBranchFilter((prev) => (prev === fromUrl.idBranch ? prev : fromUrl.idBranch));
    setCatFilter((prev) => (prev === fromUrl.idNumberCat ? prev : fromUrl.idNumberCat));
    setOnlyActive((prev) => (prev === fromUrl.onlyActive ? prev : fromUrl.onlyActive));
    setNameFilter((prev) => (prev === fromUrl.q ? prev : fromUrl.q));
    setDebouncedQ((prev) => {
      const q = (fromUrl.q || "").trim();
      return prev === q ? prev : q;
    });
  }, [searchParams]);

  const setSelectedGroupId = useCallback(
    (id) => {
      const nextId = id > 0 ? id : ROOT_UPPER;
      skipUrlSyncRef.current = true;
      setSearchParams(
        (prev) => filtersToSearchParams({ ...listFiltersLive, idGroup: nextId }, prev),
        { replace: true }
      );
    },
    [setSearchParams, listFiltersLive]
  );

  const goList = useCallback(
    (opts = {}) => {
      const { replace = false, extra } = opts;
      navigate(`/spr/number${listSearchString(listFiltersLive, extra)}`, { replace });
    },
    [navigate, listFiltersLive]
  );

  useEffect(() => {
    const el = leavesPaneRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const update = () => {
      const h = el.clientHeight;
      setTableScrollY(Math.max(160, h - TABLE_HEADER_H));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rows.length]);

  const loadItems = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    fetchSprNumberList(branchFilter || undefined)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setRows(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setError(e.message || "Не удалось загрузить номера");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [branchFilter]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    let cancelled = false;
    fetchSprNumberBranches()
      .then((data) => {
        if (!cancelled) setBranches(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setBranches([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchSprNumberCategories()
      .then((data) => {
        if (!cancelled) setCategories(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const normalized = useMemo(
    () => (Array.isArray(rows) ? rows : []).map(normalizeRow).filter((r) => r.idNumber != null),
    [rows]
  );

  const groupTree = useMemo(
    () => attachLeafCounts(buildGroupTree(normalized), normalized),
    [normalized]
  );
  const parentOptions = useMemo(
    () => flattenParentOptions(buildNumberTree(normalized)),
    [normalized]
  );
  const byId = useMemo(() => new Map(normalized.map((r) => [r.idNumber, r])), [normalized]);
  const rootLeafCount = useMemo(
    () => countDescendantLeaves(ROOT_UPPER, normalized),
    [normalized]
  );
  const nameFilterQ = nameFilter.trim().toLowerCase();

  useEffect(() => {
    if (loading || !normalized.length || uiRestoredRef.current) return;
    uiRestoredRef.current = true;

    const validGroupKeys = collectGroupKeys(groupTree);
    const ancestorsOf = (id) => {
      const path = [];
      let cur = byId.get(id);
      for (let i = 0; i < 64 && cur && cur.idUpper > 0; i += 1) {
        path.push(String(cur.idUpper));
        cur = byId.get(cur.idUpper);
      }
      return path;
    };

    let nextGroupId = selectedGroupId;
    if (nextGroupId > 0 && !validGroupKeys.has(String(nextGroupId))) {
      nextGroupId = ROOT_UPPER;
      setSelectedGroupId(ROOT_UPPER);
    }

    const savedExpanded = loadExpandedKeys().filter((k) => validGroupKeys.has(k));
    const ensurePath =
      nextGroupId > 0 ? ancestorsOf(nextGroupId).filter((k) => validGroupKeys.has(k)) : [];
    const nextExpanded = [
      ...new Set([
        ...(savedExpanded.length
          ? savedExpanded
          : groupTree.filter((node) => node.children?.length).map((node) => node.key)),
        ...ensurePath
      ])
    ];
    setExpandedKeys(nextExpanded);
    saveExpandedKeys(nextExpanded);
  }, [loading, normalized, groupTree, byId, selectedGroupId, setSelectedGroupId]);

  useEffect(() => {
    if (!uiRestoredRef.current || !groupTree.length) return;
    const valid = collectGroupKeys(groupTree);
    if (selectedGroupId > 0 && !valid.has(String(selectedGroupId))) {
      setSelectedGroupId(ROOT_UPPER);
      return;
    }
    setExpandedKeys((prev) => {
      const next = prev.filter((k) => valid.has(String(k)));
      return next.length === prev.length ? prev : next;
    });
  }, [groupTree, selectedGroupId, setSelectedGroupId]);

  const selectedGroup = useMemo(() => {
    if (selectedGroupId <= 0) return null;
    return normalized.find((r) => r.idNumber === selectedGroupId && r.isGroup) || null;
  }, [normalized, selectedGroupId]);

  const selectedGroupChildCount = useMemo(() => {
    if (selectedGroupId <= 0) return 0;
    return normalized.reduce((n, r) => (r.idUpper === selectedGroupId ? n + 1 : n), 0);
  }, [normalized, selectedGroupId]);

  const canEditGroup = Boolean(selectedGroup);
  const canDeleteGroup = canEditGroup && selectedGroupChildCount === 0;

  const leaves = useMemo(() => {
    let list = normalized.filter((r) => !r.isGroup);
    if (selectedGroupId !== ROOT_UPPER) {
      list = list.filter((r) => isUnderGroup(r, selectedGroupId, byId));
    }
    if (onlyActive) {
      list = list.filter((r) => !r.catIsDel);
    }
    if (catFilter) {
      list = list.filter((r) => r.idNumberCat === catFilter);
    }
    if (nameFilterQ) {
      list = list.filter(
        (r) =>
          String(r.name || "")
            .toLowerCase()
            .includes(nameFilterQ) ||
          String(r.catName || "")
            .toLowerCase()
            .includes(nameFilterQ)
      );
    }
    return list.map((r) => ({
      ...r,
      groupName: r.idUpper > 0 ? byId.get(r.idUpper)?.name || "" : ""
    }));
  }, [normalized, selectedGroupId, byId, onlyActive, catFilter, nameFilterQ]);

  useEffect(() => {
    if (editId == null || loading || !normalized.length) return;
    const row = normalized.find((r) => r.idNumber === editId);
    if (!row) {
      goList({ replace: true });
      return;
    }
    if (!row.isGroup) setSelectedId(editId);
  }, [editId, normalized, loading, goList]);

  const openEdit = useCallback(
    (idNumber) => {
      if (idNumber == null) return;
      navigate(`/spr/number/${idNumber}${listSearchString(listFiltersLive)}`);
    },
    [navigate, listFiltersLive]
  );

  const onGroupToggle = useCallback((key) => {
    setExpandedKeys((prev) => {
      const has = prev.includes(key);
      const next = has ? prev.filter((k) => k !== key) : [...prev, key];
      saveExpandedKeys(next);
      return next;
    });
  }, []);

  const onSelectGroup = useCallback(
    (id) => {
      setSelectedGroupId(id);
      if (id <= 0) return;
      const key = String(id);
      const hasKids = Boolean(findInTree(groupTree, id)?.children?.length);
      if (!hasKids || expandedKeys.includes(key)) return;
      const next = [...expandedKeys, key];
      setExpandedKeys(next);
      saveExpandedKeys(next);
    },
    [expandedKeys, groupTree, setSelectedGroupId]
  );

  const openCreateNumber = useCallback(() => {
    if (!canEdit) return;
    const upper = selectedGroupId > 0 ? selectedGroupId : ROOT_UPPER;
    navigate(`/spr/number/new${listSearchString(listFiltersLive, { upper })}`);
  }, [canEdit, navigate, listFiltersLive, selectedGroupId]);

  const openCreateGroup = useCallback(() => {
    if (!canEdit) return;
    const upper = selectedGroupId > 0 ? selectedGroupId : ROOT_UPPER;
    navigate(`/spr/number/new${listSearchString(listFiltersLive, { upper, group: 1 })}`);
  }, [canEdit, navigate, listFiltersLive, selectedGroupId]);

  const closeDrawer = useCallback(() => {
    goList();
  }, [goList]);

  const onSaved = useCallback(
    (saved) => {
      loadItems();
      const newId = toNumber(pick(saved || {}, "idNumber", "IdNumber"));
      if (isNew && newId != null) {
        navigate(
          `/spr/number/${newId}${listSearchString(listFiltersLive, isGroupNew ? { group: 1 } : undefined)}`,
          { replace: true }
        );
      }
    },
    [isGroupNew, isNew, loadItems, navigate, listFiltersLive]
  );

  const onDelete = useCallback(
    (record) => {
      if (!canEdit || record?.idNumber == null) return;
      const hasChildren = normalized.some((r) => r.idUpper === record.idNumber);
      if (hasChildren || (record.childCount ?? 0) > 0) {
        message.warning("Нельзя удалить: есть вложенные элементы");
        return;
      }

      Modal.confirm({
        title: "Удалить?",
        content: record.name || `№${record.idNumber}`,
        okText: "Удалить",
        cancelText: "Отмена",
        okButtonProps: { danger: true },
        onOk: () =>
          deleteSprNumber(record.idNumber)
            .then(() => {
              message.success("Удалено");
              if (selectedGroupId === record.idNumber) setSelectedGroupId(ROOT_UPPER);
              else if (editId === record.idNumber) goList();
              if (selectedId === record.idNumber) setSelectedId(null);
              setCheckedIds((prev) => prev.filter((id) => id !== record.idNumber));
              loadItems();
            })
            .catch((e) => {
              message.error(e.message || "Не удалось удалить");
              return Promise.reject(e);
            })
      });
    },
    [canEdit, editId, goList, loadItems, normalized, selectedGroupId, selectedId, setSelectedGroupId]
  );

  const branchOptions = useMemo(
    () => [
      { value: 0, label: "Все филиалы" },
      ...(branches || [])
        .map((b) => ({
          value: toNumber(pick(b, "id", "Id")),
          label: pick(b, "name", "Name") ?? ""
        }))
        .filter((o) => o.value != null && o.value > 0)
    ],
    [branches]
  );

  const categoryOptions = useMemo(
    () => [
      { value: 0, label: "Все категории" },
      ...(categories || [])
        .map((c) => ({
          value: toNumber(pick(c, "idNumberCat", "IdNumberCat")),
          label: pick(c, "name", "Name") ?? ""
        }))
        .filter((o) => o.value != null && o.value > 0)
    ],
    [categories]
  );

  const checkedRows = useMemo(
    () => leaves.filter((r) => checkedIds.includes(r.idNumber)),
    [leaves, checkedIds]
  );
  const hasChecked = checkedRows.length > 0;

  useEffect(() => {
    const valid = new Set(leaves.map((r) => r.idNumber));
    setCheckedIds((prev) => {
      const next = prev.filter((id) => valid.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [leaves]);

  const onDeleteChecked = useCallback(() => {
    if (!canEdit || !checkedRows.length) return;
    const blocked = checkedRows.filter(
      (r) => normalized.some((x) => x.idUpper === r.idNumber) || (r.childCount ?? 0) > 0
    );
    if (blocked.length) {
      message.warning("Нельзя удалить: есть вложенные элементы");
      return;
    }
    const ids = checkedRows.map((r) => r.idNumber);
    const many = ids.length > 1;
    Modal.confirm({
      title: many ? `Удалить номера (${ids.length})?` : "Удалить?",
      content: many
        ? checkedRows.map((r) => r.name || `№${r.idNumber}`).join(", ")
        : checkedRows[0].name || `№${ids[0]}`,
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          for (const id of ids) {
            await deleteSprNumber(id);
          }
          message.success("Удалено");
          if (ids.includes(editId)) goList();
          if (ids.includes(selectedId)) setSelectedId(null);
          setCheckedIds([]);
          loadItems();
        } catch (e) {
          message.error(e.message || "Не удалось удалить");
          loadItems();
          throw e;
        }
      }
    });
  }, [canEdit, checkedRows, editId, goList, loadItems, normalized, selectedId]);

  const nameColWidth = useMemo(
    () =>
      measureContentColWidth(
        "Наименование",
        leaves.map((r) => r.name),
        { min: 100, pad: 28, bodyFont: `400 13px Manrope, "Segoe UI", Tahoma, sans-serif` }
      ),
    [leaves]
  );

  const catColWidth = useMemo(
    () =>
      measureContentColWidth(
        "Категория",
        leaves.map((r) => r.catName),
        { min: 100, pad: 20 }
      ),
    [leaves]
  );

  const chessColWidth = useMemo(
    () =>
      measureContentColWidth(
        "Шахматка",
        leaves.map((r) => formatDate(r.dateDaysMax)),
        { min: 0, pad: 32 }
      ),
    [leaves]
  );

  const allColumns = useMemo(
    () => [
      {
        title: "",
        label: "Статус",
        key: "status",
        width: 44,
        align: "center",
        render: (_, record) => {
          const status = roomStatus(record);
          const tone = status === "inactive" ? "off" : "on";
          return (
            <Tooltip title={STATUS_LABEL[status]}>
              <span
                className="spr-numbers__status-dot"
                data-tone={tone}
                aria-label={STATUS_LABEL[status]}
              />
            </Tooltip>
          );
        }
      },
      {
        title: "Наименование",
        dataIndex: "name",
        key: "name",
        width: nameColWidth,
        ellipsis: true,
        className: "spr-numbers__col-name"
      },
      {
        title: "Категория",
        dataIndex: "catName",
        key: "catName",
        width: catColWidth,
        ellipsis: true,
        className: "spr-numbers__col-cat"
      },
      {
        title: "Площадь",
        key: "square",
        width: 90,
        render: (_, record) => formatSquare(record.square)
      },
      {
        title: "Мест",
        key: "capacity",
        width: 64,
        render: (_, record) => {
          const text = formatCapacity(record.cntMest, record.cntDopMest);
          return text ? <span className="spr-numbers__cap">{text}</span> : null;
        }
      },
      {
        title: "Заселение",
        key: "timeIn",
        width: 90,
        align: "center",
        render: (_, record) => (record.isGroup ? null : formatTime(record.timeIn))
      },
      {
        title: "Выселение",
        key: "timeOut",
        width: 90,
        align: "center",
        render: (_, record) => (record.isGroup ? null : formatTime(record.timeOut))
      },
      {
        title: "Замок",
        key: "idLock",
        width: 80,
        align: "right",
        render: (_, record) => (!record.isGroup && record.idLock ? record.idLock : null)
      },
      {
        title: "Онлайн",
        key: "online",
        width: 72,
        align: "center",
        render: (_, record) =>
          !record.isGroup && record.online ? (
            <Tooltip title="Продавать онлайн">
              <span className="spr-numbers__online" aria-label="Продавать онлайн">✓</span>
            </Tooltip>
          ) : null
      },
      {
        title: "Группа",
        dataIndex: "groupName",
        key: "groupName",
        width: 110,
        ellipsis: true
      },
      {
        title: "Филиал",
        dataIndex: "bName",
        key: "bName",
        width: 140,
        ellipsis: true
      },
      {
        title: "Шахматка",
        dataIndex: "dateDaysMax",
        key: "dateDaysMax",
        width: chessColWidth,
        className: "spr-numbers__col-date",
        render: (value) => {
          const text = formatDate(value);
          return text ? <span className="spr-numbers__date">{text}</span> : null;
        }
      }
    ],
    [nameColWidth, catColWidth, chessColWidth]
  );

  const columns = useMemo(
    () => applyGridSettings(allColumns, gridSettings),
    [allColumns, gridSettings]
  );

  const tableScrollX = useMemo(
    () => columns.reduce((sum, col) => sum + (Number(col.width) || 0), 0),
    [columns]
  );

  const onGridSettingsChange = useCallback((next) => {
    setGridSettings(next);
  }, []);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: checkedIds,
      columnWidth: 36,
      onChange: (keys) => {
        setCheckedIds(keys.map(Number).filter(Number.isFinite));
      }
    }),
    [checkedIds]
  );

  const createUpper = selectedGroupId > 0 ? selectedGroupId : ROOT_UPPER;
  const drawerIdUpper = newIdUpper != null ? newIdUpper : createUpper;
  const rootSelected = selectedGroupId === ROOT_UPPER;
  const editingRow = editId == null ? null : normalized.find((r) => r.idNumber === editId) || null;
  const isGroupCard = isGroupNew || Boolean(editingRow?.isGroup);

  if (!canEdit) {
    return (
      <div className="spr-numbers">
        <Alert type="warning" showIcon message="Нет права «Настройка номеров»" />
      </div>
    );
  }

  return (
    <div className="spr-numbers">
      {error ? <Alert className="spr-numbers__error" type="error" showIcon message={error} /> : null}
      <div className="spr-numbers__body">
        <Splitter
          className="spr-numbers__splitter"
          onResize={(sizes) => {
            const next = Number(sizes?.[0]);
            if (!Number.isFinite(next)) return;
            setGroupsWidth(
              Math.min(GROUPS_WIDTH_MAX, Math.max(GROUPS_WIDTH_MIN, Math.round(next)))
            );
          }}
          onResizeEnd={(sizes) => {
            const next = Number(sizes?.[0]);
            if (!Number.isFinite(next)) return;
            const width = Math.min(
              GROUPS_WIDTH_MAX,
              Math.max(GROUPS_WIDTH_MIN, Math.round(next))
            );
            setGroupsWidth(width);
            try {
              localStorage.setItem(GROUPS_WIDTH_KEY, String(width));
            } catch {
              /* ignore */
            }
          }}
        >
          <Splitter.Panel
            size={groupsWidth}
            min={GROUPS_WIDTH_MIN}
            max={GROUPS_WIDTH_MAX}
            className="spr-numbers__groups-panel"
          >
            <div className="spr-numbers__groups">
              <div className="spr-numbers__groups-head">
                <p className="spr-numbers__groups-title">Структура фонда</p>
                <div className="spr-numbers__groups-actions">
                  <Tooltip title="Добавить группу">
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      aria-label="Добавить группу"
                      onClick={openCreateGroup}
                    />
                  </Tooltip>
                  <Tooltip title={canEditGroup ? "Изменить группу" : "Выберите группу"}>
                    <span>
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        aria-label="Изменить группу"
                        disabled={!canEditGroup}
                        onClick={() => openEdit(selectedGroupId)}
                      />
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !canEditGroup
                        ? "Выберите группу"
                        : canDeleteGroup
                          ? "Удалить группу"
                          : "Нельзя удалить: есть вложенные элементы"
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label="Удалить группу"
                        disabled={!canDeleteGroup}
                        onClick={() => selectedGroup && onDelete(selectedGroup)}
                      />
                    </span>
                  </Tooltip>
                </div>
              </div>
              <div className="spr-numbers__groups-tree">
                {loading && !rows.length ? (
                  <div className="spr-numbers__center">
                    <Spin tip="Загрузка..." />
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className={`spr-numbers__tree-node${rootSelected ? " spr-numbers__tree-node--selected" : ""}`}
                      onClick={() => setSelectedGroupId(ROOT_UPPER)}
                    >
                      <span className="spr-numbers__tree-main">
                        <TreeIcon src={rootSelected ? folderOpenSrc : folderSrc} size={16} />
                        <span className="spr-numbers__tree-name">Все номера</span>
                      </span>
                      <span className="spr-numbers__tree-count">{rootLeafCount}</span>
                    </button>
                    <GroupTreeList
                      nodes={groupTree}
                      depth={1}
                      selectedGroupId={selectedGroupId}
                      editId={editId}
                      expandedKeys={expandedKeys}
                      onSelect={onSelectGroup}
                      onToggle={onGroupToggle}
                    />
                  </>
                )}
              </div>
            </div>
          </Splitter.Panel>
          <Splitter.Panel className="spr-numbers__leaves-panel" min={360}>
            <div className="spr-numbers__leaves">
              <div className="spr-numbers__toolbar">
                <div className="spr-numbers__extra-actions">
                  <Tooltip title="Создать номер">
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      aria-label="Создать номер"
                      onClick={openCreateNumber}
                    />
                  </Tooltip>
                  <Tooltip title={hasChecked ? "Удалить" : "Отметьте номера"}>
                    <span>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        disabled={!hasChecked}
                        onClick={onDeleteChecked}
                      />
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={hasChecked ? "Заполнить график занятости" : "Отметьте номера"}
                  >
                    <span>
                      <Button
                        icon={<CalendarOutlined />}
                        aria-label="Заполнить график занятости"
                        disabled={!hasChecked}
                        onClick={() => setFillOpen(true)}
                      />
                    </span>
                  </Tooltip>
                  <GridSettings
                    title="Настройка таблицы"
                    storageKey={GRID_STORAGE_KEY}
                    columns={allColumns}
                    value={gridSettings}
                    onChange={onGridSettingsChange}
                  />
                </div>
                <NumbersSearchFilter
                  name={nameFilter}
                  onNameChange={setNameFilter}
                  catId={catFilter}
                  onCatChange={setCatFilter}
                  catOptions={categoryOptions.filter((o) => o.value > 0)}
                  onlyActive={onlyActive}
                  onOnlyActiveChange={setOnlyActive}
                />
                {branchOptions.length > 1 ? (
                  <label className="spr-numbers__field">
                    <span className="spr-numbers__field-label">Филиал</span>
                    <Select
                      className="spr-numbers__branch"
                      options={branchOptions}
                      value={branchFilter || 0}
                      suffixIcon={<Chevron />}
                      popupMatchSelectWidth={false}
                      onChange={(v) => setBranchFilter(v && v > 0 ? v : null)}
                    />
                  </label>
                ) : null}
              </div>
              <div className="spr-numbers__table-card">
                {loading && !rows.length ? (
                  <div className="spr-numbers__center">
                    <Spin tip="Загрузка номеров..." />
                  </div>
                ) : (
                  <div
                    className="spr-numbers__table-wrap"
                    ref={leavesPaneRef}
                    style={{ "--spr-numbers-table-x": `${tableScrollX}px` }}
                  >
                    <Table
                      className="spr-numbers__table"
                      size="small"
                      rowKey="idNumber"
                      columns={columns}
                      dataSource={leaves}
                      loading={loading}
                      pagination={false}
                      scroll={{ x: tableScrollX, y: tableScrollY }}
                      rowSelection={rowSelection}
                      onRow={(record) => ({
                        onClick: (e) => {
                          if (e.target.closest(".ant-checkbox-wrapper, .ant-table-selection-column")) {
                            return;
                          }
                          openEdit(record.idNumber);
                        }
                      })}
                      rowClassName={(record) =>
                        [
                          "spr-numbers__row",
                          selectedId != null && record.idNumber === selectedId
                            ? "spr-numbers__row--selected"
                            : "",
                          editId != null && record.idNumber === editId
                            ? "spr-numbers__row--editing"
                            : ""
                        ]
                          .filter(Boolean)
                          .join(" ")
                      }
                      locale={{
                        emptyText: nameFilterQ || catFilter
                          ? "Нет номеров по фильтру"
                          : selectedGroupId === ROOT_UPPER
                            ? "Нет номеров"
                            : "В группе нет номеров"
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </Splitter.Panel>
        </Splitter>
      </div>
      <SprNumber
        open={drawerOpen}
        idNumber={editId}
        isNew={isNew}
        isGroup={isGroupCard}
        idUpper={drawerIdUpper}
        parentOptions={parentOptions}
        onClose={closeDrawer}
        onSaved={onSaved}
      />
      <SprNumberFillModal
        open={fillOpen}
        numbers={checkedRows}
        onClose={() => setFillOpen(false)}
        onFilled={loadItems}
      />
    </div>
  );
};

export default SprNumbers;
